import { Theme, css } from '@emotion/react';
import { TockCookieName } from '@tock/shared/constants/browser-storage/TockCookies';
import { computeEnv, envType } from 'settings/AppSettings';
import { getCookie } from '@tock/shared/utils/cookieUtils';
import { getTurnstileSiteKeySelector } from 'selectors/appSelector';
import { isInApp } from 'utils/deviceUtils';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import React from 'react';
import ScriptLoader from '@tock/shared/components/render-prop-components/ScriptLoader';
import queryString from '@tock/query-string';

const TURNSTILE_DIV_ID = 'turnstile-container';
const TURNSTYLE_DIV_SELECTOR = `#${TURNSTILE_DIV_ID}`;

interface TurnstileValidatorProps {
  onReceiveChallengeResponse(challengeResponse: string): void;
}

// Allow application of an "override" site key entered as a query param
function getEffectiveSiteKey(siteKey: string, fallbackKey?: string) {
  if (computeEnv(__ENV__) === envType.PROD) {
    return siteKey;
  }

  return fallbackKey ?? siteKey;
}

export function isTurnstileActive(isFeatureEnabled: boolean) {
  const tockTestCookie = BROWSER && !!getCookie(TockCookieName.TOCK_TEST);
  return isFeatureEnabled && !isInApp() && !tockTestCookie;
}

export type TokenRefreshHandle = {
  refreshToken: () => void;
};

/**
 * Renders a widget that manages user validation (i.e., captcha) through Cloudflare's Turnstile offering
 */
const TurnstileValidator = React.forwardRef<TokenRefreshHandle, TurnstileValidatorProps>(
  ({ onReceiveChallengeResponse }, ref) => {
    const [widgetId, setWidgetId] = React.useState(null);
    const [isWidgetVisible, setIsWidgetVisible] = React.useState(false);

    React.useImperativeHandle(ref, () => ({
      refreshToken() {
        if (window.turnstile && widgetId) {
          // Clear the existing challenge response
          onReceiveChallengeResponse('');
          window.turnstile.reset(widgetId);
        }
      },
    }));

    const { search } = useLocation();
    const testOverrideSiteKey = queryString.parse(search).turnstileSiteKey;

    const siteKey = useSelector(getTurnstileSiteKeySelector);

    React.useEffect(() => {
      const effectiveSiteKey = getEffectiveSiteKey(siteKey, testOverrideSiteKey);

      const renderParams = {
        sitekey: effectiveSiteKey,
        theme: 'light',
        appearance: 'interaction-only',
        action: 'sign-up',
        'error-callback': (e: unknown) => undefined, // TODO: redirect to hiccups page,
        'before-interactive-callback': () => setIsWidgetVisible(true),
        'after-interactive-callback': () => setIsWidgetVisible(false),
        callback: (challengeResponse: string) => onReceiveChallengeResponse(challengeResponse),
      };

      // If the script has already been loaded but the widget isn't currently rendered
      if (window.turnstile && !widgetId) {
        const widgetId = window.turnstile.render(TURNSTYLE_DIV_SELECTOR, renderParams);
        setWidgetId(widgetId);
      }

      // Callback function to render widget on script load
      window.onloadTurnstileCallback = () => {
        const widgetId = window.turnstile.render(TURNSTYLE_DIV_SELECTOR, renderParams);
        setWidgetId(widgetId);
      };
    }, [onReceiveChallengeResponse, siteKey, testOverrideSiteKey, widgetId]);

    // Remove the widget when the component unmounts
    React.useEffect(() => {
      return () => {
        window.turnstile?.remove();
        setWidgetId(null);
      };
    }, []);

    return (
      <>
        <ScriptLoader
          async={true}
          scripts={{
            cloudflareTurnstile:
              'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback',
          }}
          retryAttempts={3}
        />
        <div
          css={(theme: Theme) => [
            css`
              iframe {
                display: block;
                width: 100% !important;
              }
            `,
            isWidgetVisible &&
              css`
                margin-top: ${theme.spacing(3)}px;
              `,
          ]}
          id={TURNSTILE_DIV_ID}
        />
      </>
    );
  }
);

export default TurnstileValidator;
